import React, { createContext, useMemo, useState } from 'react'
import { useUserSettings } from 'src/Hooks'

export enum PresetChartType {
  Building = 'building',
  Consumption = 'consumption',
}

export type PresetChartTypes = `${PresetChartType}`

type PerformanceAndConsumptionChartsContextValue = {
  presetChartToDisplay: PresetChartTypes | null
  setPresetChartToDisplay: (chart: PresetChartTypes) => void
}

/**
 * Global context for performance and consumption charts
 */
const PerformanceAndConsumptionChartsContext = createContext<PerformanceAndConsumptionChartsContextValue>({
  presetChartToDisplay: null,
  setPresetChartToDisplay: () => undefined,
})

export const PerformanceAndConsumptionChartsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userSettings } = useUserSettings()
  const initialPresetChart = (userSettings?.presetChart as PresetChartTypes) || PresetChartType.Building
  const [presetChartToDisplay, setPresetChartToDisplay] = useState<PresetChartTypes>(initialPresetChart)

  const value = useMemo(
    () => ({
      presetChartToDisplay,
      setPresetChartToDisplay,
    }),
    [presetChartToDisplay, setPresetChartToDisplay]
  )

  return <PerformanceAndConsumptionChartsContext.Provider value={value}>{children}</PerformanceAndConsumptionChartsContext.Provider>
}

export const usePerformanceAndConsumptionChartsContext = (): PerformanceAndConsumptionChartsContextValue => {
  const context = React.useContext(PerformanceAndConsumptionChartsContext)
  if (!context) {
    throw new Error('usePerformanceAndConsumptionChartsContext must be used within a PerformanceAndConsumptionChartsProvider')
  }
  return context
}
